import { IonIcon } from '@ionic/react';
import { arrowForward } from 'ionicons/icons';
import React from 'react';

import { UiTypography } from '@/components/ui/Typography';
import { PAYWALL_TIMER_MINUTES, SELECT_PLAN_SECTION_ID } from '@/constants';
import { useStripeV2Plans } from '@/hooks/useStripeV2Plans';
import { LimitedTimerHoc } from '@/pages/Paywall/components/LimitedTimerHoc';
import { PaymentDescription } from '@/pages/Paywall/components/PaymentDescription/Description';

import {
  CardBorderWrapper,
  CardContent,
  CardDescription,
  CardInner,
  CardPriceRow,
  CardTitle,
  GetPremiumButton,
  HeartsImage,
  OfferCardSection,
  OfferHeadline,
  TimerBlock,
  TimerBlockInner,
  TimerLabel,
  TimerRow,
  TimerValue,
  UniqueGradientText,
} from './OfferCardSelectPlan.styles';

const formatPrice = (value: number) => `$${value.toFixed(2)}`;

export const OfferCardSelectPlan: React.FC<{
  buttonProps?: {
    text: string;
    onClick: () => void;
  };
  sectionId?: string;
}> = ({ buttonProps, sectionId = SELECT_PLAN_SECTION_ID }) => {
  const timeToExpire = PAYWALL_TIMER_MINUTES * 60 * 1000;
  const { selectedPlan, stripePlans, isLoadingPlans, monthlyPlan } =
    useStripeV2Plans();
  const plan = selectedPlan ?? stripePlans[0];

  return (
    <OfferCardSection id={sectionId}>
      <OfferHeadline>
        REDEEM YOUR <UniqueGradientText>UNIQUE</UniqueGradientText>
        <br />
        ONE-TIME OFFER NOW!
      </OfferHeadline>
      <CardBorderWrapper>
        <CardInner>
          <CardContent>
            <HeartsImage />
            <CardTitle>
              <UiTypography variant="title-medium" color="white">
                Week of Feelings
              </UiTypography>
            </CardTitle>
            <CardDescription>
              <UiTypography variant="body-medium" color="neutral-white">
                No limits. No regrets.
                <br />
                Just seven unforgettable days.
              </UiTypography>
            </CardDescription>
            <LimitedTimerHoc timeToExpire={timeToExpire}>
              {({ minutes, seconds }) => {
                const units = [
                  { value: minutes, label: 'min' },
                  { value: seconds, label: 'sec' },
                ] as const;
                return (
                  <TimerRow>
                    {units.map(({ value, label }) => (
                      <TimerBlock key={label}>
                        <TimerBlockInner>
                          <TimerValue>{value}</TimerValue>
                          <TimerLabel>
                            <UiTypography
                              variant="body-small"
                              color="neutral-white"
                            >
                              {label}
                            </UiTypography>
                          </TimerLabel>
                        </TimerBlockInner>
                      </TimerBlock>
                    ))}
                  </TimerRow>
                );
              }}
            </LimitedTimerHoc>
            {!isLoadingPlans && plan && (
              <CardPriceRow>
                <UiTypography
                  variant="body-medium"
                  color="neutral-white"
                  decoration="line-through"
                >
                  {formatPrice(plan.original_price)}
                </UiTypography>
                <IonIcon
                  icon={arrowForward}
                  style={{
                    color: 'var(--text-color-neutral-white, #fff)',
                  }}
                />
                <UiTypography variant="title-large" color="white">
                  {formatPrice(plan.price)}
                </UiTypography>
              </CardPriceRow>
            )}
          </CardContent>
        </CardInner>
      </CardBorderWrapper>
      {!isLoadingPlans && (
        <>
          <GetPremiumButton type="button" onClick={buttonProps?.onClick}>
            {buttonProps?.text ?? 'GET PREMIUM'}
          </GetPremiumButton>
          {selectedPlan && (
            <PaymentDescription
              selectedPlan={selectedPlan}
              monthlyPlan={monthlyPlan}
            />
          )}
        </>
      )}
    </OfferCardSection>
  );
};
